import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadFestivalsApiFirst, loadGuidesByCategory } from '../../utils/cmsContentLoader';
import { Seo, Loading } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import DiscoverySection from '../explore/DiscoverySection';
import FaqAccordion from '../explore/FaqAccordion';
import KnowledgeFlow from '../explore/KnowledgeFlow';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/explore.scss';
import '../../styles/guides.scss';
import '../../styles/tourist-nav.scss';

const DATE_TYPE_NOTE = {
	lunar_calculated: 'date shifts yearly with the lunar calendar',
	variable: 'date varies by year',
	fixed: null,
	solar_fixed: 'fixed solar calendar date',
};

const FESTIVAL_FAQS = [
	{ q: 'Why do festival dates change every year?', a: 'Most major Nepali festivals follow lunar or lunisolar calendars. Dashain, Tihar, Holi, and Buddha Jayanti land on different Gregorian dates each year — sometimes shifting by weeks. Always confirm the specific date for your travel year.' },
	{ q: 'Can tourists participate in festivals?', a: 'Some festivals are genuinely open for respectful observation (Kathmandu Valley chariot processions, Holi in public squares). Others are private family observances — be a quiet bystander, not a participant, unless invited.' },
	{ q: 'How do festivals affect my trip?', a: 'Dashain and Tihar disrupt transport and close some businesses but offer extraordinary cultural experiences. Book accommodation early and build buffer days into your schedule.' },
	{ q: 'Are there festivals beyond the famous three?', a: 'Yes — Nepal has hundreds of local festivals. Bisket Jatra (Bhaktapur), Indra Jatra (Kathmandu), Teej, Buddha Jayanti, and village-specific observances happen throughout the year. We are expanding coverage beyond the major nationwide festivals.' },
];

function FestivalsHubPage() {
	const [festivals, setFestivals] = useState([]);
	const [guides, setGuides] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		Promise.all([
			loadFestivalsApiFirst(),
			loadGuidesByCategory('festival'),
		])
			.then(([festList, guideList]) => {
				setFestivals(festList);
				setGuides(guideList);
			})
			.finally(() => setLoading(false));
	}, []);

	if (loading) return <Loading />;

	return (
		<div className="explore-page tourist-page">
			<Seo
				title="Nepal Festivals — Calendar, Culture & How to Experience Them"
				path="/festivals"
				description="Complete guide to Nepal's festivals — Dashain, Tihar, Holi, village celebrations, lunar calendar dates, cultural etiquette, photography tips, and how to plan your trip around them."
			/>

			<ExploreHero
				image={EXPLORE_IMAGES.festival}
				eyebrow="Plan your trip"
				title="Nepal's festival calendar"
				lead="From Dashain's nationwide celebration to village chariot processions — when they happen, where to experience them, and how to participate respectfully."
				primaryCta={{ to: '#calendar', label: 'Browse festivals' }}
				secondaryCta={{ to: '/seasonal-discovery', label: 'When to visit' }}
				compact
			/>

			<QuickFactsBar facts={[
				{ value: 'Lunar', label: 'Date system' },
				{ value: 'Dashain', label: 'Biggest festival' },
				{ value: 'Oct–Nov', label: 'Peak season overlap' },
				{ value: 'Valley', label: 'Best public events' },
			]} />

			<div className="explore-body">
				<DiscoverySection
					title="Understanding Nepal's festival calendar"
					intro="Most festivals follow lunar calendars — plan around the season, then confirm exact dates closer to travel."
				>
					{guides.map((g) => (
						<div key={g.slug} className="info-card" style={{ maxWidth: '780px' }}>
							<div className="info-card__body">
								<h3 className="info-card__title">{g.title}</h3>
								<p className="info-card__text">{g.summary}</p>
								<Link to={`/guides/${g.slug}`} className="season-card__link">Read the full guide →</Link>
							</div>
						</div>
					))}
				</DiscoverySection>

				<DiscoverySection id="calendar" eyebrow="Major celebrations" title="Festivals across Nepal" intro="Each page covers the story, history, when and where it happens, how to experience it, and photography etiquette." alt>
					<div className="guide-grid">
						{festivals.map((f) => (
							<Link key={f.slug} to={`/festivals/${f.slug}`} className="guide-card guide-card--with-media">
								<div className="info-card__image" style={{ height: 140 }}>
									<img src={EXPLORE_IMAGES.festival} alt="" loading="lazy" />
								</div>
								<div className="guide-card__body">
									<h3 className="guide-card__title">{f.name}</h3>
									{f.tagline && <p className="guide-card__summary">{f.tagline}</p>}
									{f.typical_month && (
										<span className="guide-card__flag">
											<i className="fas fa-calendar-alt" aria-hidden="true" /> {f.typical_month}
											{DATE_TYPE_NOTE[f.date_type] ? ` — ${DATE_TYPE_NOTE[f.date_type]}` : ''}
										</span>
									)}
								</div>
							</Link>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection eyebrow="Common questions" title="Festival FAQs">
					<FaqAccordion items={FESTIVAL_FAQS} />
				</DiscoverySection>

				<KnowledgeFlow
					title="Plan around festivals"
					steps={[
						{ label: 'When should I visit Nepal?', to: '/seasonal-discovery', desc: 'Month-by-month guide with festival calendar' },
						{ label: 'Explore cultural destinations', to: '/destinations?tag=unesco-heritage', desc: 'Temples, stupas, and durbar squares' },
						{ label: 'Understand visa & entry', to: '/government', desc: 'Immigration and permit requirements' },
						{ label: 'Book a cultural tour', to: '/packages', desc: 'Private tours timed to festival dates' },
					]}
				/>
			</div>
		</div>
	);
}

export default FestivalsHubPage;
